import { type FormEvent, type ReactNode, useRef, useState } from 'react';

import type { RouteElement } from '@shared/router';
import Link from '@shared/router/link';
import * as history from '@shared/router/location';

import { Button } from '@playbook/atoms/button';
import { Card } from '@playbook/atoms/card';
import { TextInput } from '@playbook/atoms/inputs/text-input';

import { ApiError, wasRefused } from '@portal/pkg/api';
import { useKeyActions } from '@portal/pkg/api/keys';
import { Action, type Key } from '@portal/pkg/api/types';
import { generateKeypair } from '@portal/pkg/crypto/keypair';
import * as routes from '@portal/pkg/routes';
import { useSession } from '@portal/pkg/session';
import { Notice } from '@portal/views/_shared/notice';
import { PageHeader, PortalPage } from '@portal/views/_shared/page';
import { CapabilityPicker } from '@portal/views/keys/CapabilityPicker';
import { ExpiryPicker, isExpiryValid } from '@portal/views/keys/ExpiryPicker';
import { KeyIssued } from '@portal/views/keys/KeyIssued';
import { UnsavedPrivateKey } from '@portal/views/keys/UnsavedPrivateKey';
import {
	KeyMaterialChoice,
	type KeyMaterialSource,
	looksLikePublicKey,
} from '@portal/views/keys/KeyMaterialChoice';
import { NotPermitted, can } from '@portal/views/keys/_shared';

import css from './styles.module.css';

/** What the API's own validation allows, so a longer name is refused here. */
const MaxNameLength = 255 as const;

export default function CreateKeyPage(): RouteElement {
	const { createKey } = useKeyActions();
	const { organization } = useSession();

	const [name, setName] = useState('');
	// Nothing preselected: what a key may read is the decision this form is
	// for, and a default is a decision made for someone who did not look.
	const [capabilities, setCapabilities] = useState<string[]>([]);
	const [source, setSource] = useState<KeyMaterialSource>('generate');
	const [pastedKey, setPastedKey] = useState('');
	const [expiryChoice, setExpiryChoice] = useState('0');
	const [expiresAt, setExpiresAt] = useState(0);

	const [issued, setIssued] = useState<Nullable<Key>>(null);
	const [error, setError] = useState<Nullable<string>>(null);
	const [submitting, setSubmitting] = useState(false);

	/*
	 *	A ref, not state: the private key must not be a render input, must not
	 *	show up in the component's state in React's devtools on every render,
	 *	and must not be liftable into a parent. It is read by the screens below
	 *	and dies with this component.
	 *
	 *	The public half rides alongside it so a retry sends the same pair. A
	 *	retry that generated a new one would leave the key the first attempt
	 *	may already have created holding a public half nobody has the match to.
	 */
	const privateKey = useRef<Nullable<string>>(null);
	const generatedKey = useRef<Nullable<string>>(null);

	const nameTooLong = name.length > MaxNameLength;

	// A key with no access cannot be used, and a paste that is not a public
	// key is not one to send.
	const submittable =
		!nameTooLong &&
		capabilities.length > 0 &&
		isExpiryValid(expiryChoice, expiresAt) &&
		(source === 'generate' || looksLikePublicKey(pastedKey));

	async function handleSubmit(event: FormEvent): Promise<void> {
		event.preventDefault();

		setError(null);
		setSubmitting(true);

		try {
			let publicKey = pastedKey;

			if (source === 'generate') {
				if (!generatedKey.current || !privateKey.current) {
					const generated = await generateKeypair();

					generatedKey.current = generated.publicKey;
					privateKey.current = generated.privateKey;
				}

				publicKey = generatedKey.current;
			}

			setIssued(await createKey({ name, publicKey, capabilities, expiresAt }));
		} catch (caught) {
			// Only a refusal is proof nothing was written. Anything else may
			// have created the key and lost the answer on the way back, and
			// then the private half in memory is the only copy there will ever
			// be — so it stays, and the screen below offers it.
			if (wasRefused(caught)) {
				privateKey.current = null;
				generatedKey.current = null;
			}

			setError(createError(caught));
		} finally {
			setSubmitting(false);
		}
	}

	// The result comes first. Once the key exists, this screen is the only
	// place its private half is ever shown, and no later change of permission
	// is a reason to take that away.
	if (issued) {
		return (
			<PortalPage width="narrow">
				<PageHeader
					title={issued.name}
					description="Created just now. The private key is below, and this is the only time it exists anywhere but your own machine."
				/>
				<KeyIssued issued={issued} privateKey={privateKey.current} />
			</PortalPage>
		);
	}

	if (!can(organization, Action.CreateKey)) {
		return (
			<PortalPage width="narrow">
				<PageHeader title="New API key" breadcrumb={<Breadcrumb />} />
				<NotPermitted what="create a key" />
			</PortalPage>
		);
	}

	return (
		<PortalPage width="narrow">
			<PageHeader
				title="New API key"
				description="The key pair is made in your browser when you submit — we only ever receive the public half."
				breadcrumb={<Breadcrumb />}
			/>

			<Card>
				<form className={css.form} onSubmit={handleSubmit}>
					{error && (
						<Notice variant="error" title="The key was not created">
							{error}
						</Notice>
					)}

					{error && privateKey.current && (
						<UnsavedPrivateKey name={name} privateKey={privateKey.current} />
					)}

					<TextInput
						required
						name="name"
						label="Name"
						status={nameTooLong ? 'error' : undefined}
						message={
							nameTooLong ? `At most ${MaxNameLength} characters.` : undefined
						}
						value={name}
						placeholder="Attendance export"
						onChange={(event) => setName(event.target.value)}
					/>

					<CapabilityPicker
						selected={capabilities}
						label="Access"
						description="Rotating copies whatever you pick. You can change it later without rotating."
						onChange={setCapabilities}
					/>

					<ExpiryPicker
						expiresAt={expiresAt}
						choice={expiryChoice}
						onChange={(choice, chosen) => {
							setExpiryChoice(choice);
							setExpiresAt(chosen);
						}}
					/>

					<KeyMaterialChoice
						source={source}
						publicKey={pastedKey}
						onSourceChange={setSource}
						onPublicKeyChange={setPastedKey}
					/>

					<div className={css.actions}>
						<Button
							label="Cancel"
							onClick={() => history.push(routes.Keys())}
						/>
						<Button
							variant="primary"
							type="submit"
							label="Create key"
							loading={submitting}
							disabled={submitting || !submittable}
						/>
					</div>
				</form>
			</Card>
		</PortalPage>
	);
}

function Breadcrumb(): ReactNode {
	return (
		<Link href={routes.Keys()}>
			<span className={css.breadcrumb}>← API keys</span>
		</Link>
	);
}

function createError(caught: unknown): string {
	if (wasRefused(caught)) {
		return (caught as ApiError).message;
	}

	return 'Could not reach the server, so the key may have been created. Check your keys before trying again — if one is there you cannot use, revoke it.';
}
